import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { trashIcon } from '@progress/kendo-svg-icons';
import { useFormBuilder } from '../context/FormBuilderContext';
import type { Section } from '../types';
import { encodeDragId } from '../utils/dnd';
import { FieldBlock } from './FieldBlock';

export function SectionBlock({ section, depth }: { section: Section; depth: number }) {
  const { document, updateSection, removeSection, addSection, isEditable } = useFormBuilder();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: encodeDragId({ kind: 'section', sectionId: section.id }),
    disabled: !isEditable,
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: encodeDragId({ kind: 'section-body', sectionId: section.id }),
  });

  const sortedFields = [...section.fields].sort((a, b) => a.order - b.order);
  const fieldIds = sortedFields.map((f) => encodeDragId({ kind: 'field', fieldId: f.id }));

  // Replaces legacy Category.parent_id structural nesting — children render recursively,
  // each reordered only among its own siblings (see reorderSections in FormBuilderContext).
  const childSections = document.sections.filter((s) => s.parentId === section.id).sort((a, b) => a.order - b.order);
  const childSectionIds = childSections.map((s) => encodeDragId({ kind: 'section', sectionId: s.id }));

  return (
    <div
      ref={setNodeRef}
      className="k-card"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        marginBottom: 'var(--kendo-spacing-3)',
        marginLeft: depth > 0 ? 'var(--kendo-spacing-5)' : 0,
      }}
    >
      <div className="k-card-header" style={{ display: 'flex', alignItems: 'center', gap: 'var(--kendo-spacing-2)' }}>
        {isEditable && (
          <span {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--kendo-color-subtle)' }} title="Drag to reorder section">
            ⠿⠿
          </span>
        )}
        <Input
          value={section.label ?? section.name}
          onChange={(e) => updateSection(section.id, { label: String(e.value ?? '') })}
          disabled={!isEditable}
          // minWidth: 0 overrides the browser's default min-width:auto on flex items —
          // without it this Input expands to its intrinsic width and pushes the
          // Subsection/Delete buttons outside the header, where .k-card-header's
          // overflow:hidden clips them entirely (they're not just squeezed, they vanish).
          style={{ flex: '1 1 0%', minWidth: 0, fontWeight: 'var(--kendo-font-weight-bold)' }}
        />
        {isEditable && (
          <>
            <Button fillMode="flat" size="small" onClick={() => addSection(section.id)} title="Add a nested subsection">
              + Subsection
            </Button>
            <Button fillMode="flat" svgIcon={trashIcon} onClick={() => removeSection(section.id)} title="Remove section" />
          </>
        )}
      </div>

      <div
        ref={setDropRef}
        className="k-card-body"
        style={{ background: isOver ? 'var(--kendo-color-primary-subtle)' : 'transparent' }}
      >
        {/* rectSortingStrategy (not verticalListSortingStrategy) computes drag positions from
            actual rendered rects, which is what makes reordering work correctly in a wrapping
            row layout instead of assuming every item is a full-width line. */}
        <SortableContext items={fieldIds} strategy={rectSortingStrategy}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--kendo-spacing-2)' }}>
            {sortedFields.map((field) => (
              <FieldBlock key={field.id} field={field} />
            ))}
          </div>
        </SortableContext>
        {sortedFields.length === 0 && childSections.length === 0 && (
          <div
            style={{
              fontSize: 'var(--kendo-font-size-xs)',
              color: 'var(--kendo-color-subtle)',
              textAlign: 'center',
              padding: 'var(--kendo-spacing-2) 0',
            }}
          >
            Drag a field here from the palette
          </div>
        )}

        {childSections.length > 0 && (
          <SortableContext items={childSectionIds} strategy={verticalListSortingStrategy}>
            <div style={{ marginTop: sortedFields.length > 0 ? 'var(--kendo-spacing-3)' : 0 }}>
              {childSections.map((child) => (
                <SectionBlock key={child.id} section={child} depth={depth + 1} />
              ))}
            </div>
          </SortableContext>
        )}
      </div>
    </div>
  );
}
